import React from 'react';
import { NailShape } from '../types';

interface NailArtPreviewProps {
  productId: string;
  shape?: NailShape;
  className?: string;
  showAllFive?: boolean;
}

export const NailArtPreview: React.FC<NailArtPreviewProps> = ({
  productId,
  shape = 'Almond',
  className = 'w-full h-56',
  showAllFive = false,
}) => {
  // Shape clip path / border radius styling
  const getShapeClasses = (s: NailShape) => {
    switch (s) {
      case 'Almond':
        return 'rounded-t-[40px] rounded-b-[14px]';
      case 'Coffin':
        return 'rounded-t-[8px] rounded-b-[12px] [clip-path:polygon(15%_0%,85%_0%,100%_100%,0%_100%)]';
      case 'Oval':
        return 'rounded-t-[30px] rounded-b-[18px]';
      case 'Square':
        return 'rounded-t-[6px] rounded-b-[8px]';
      case 'Stiletto':
        return 'rounded-t-[50px] rounded-b-[12px] [clip-path:polygon(50%_0%,100%_100%,0%_100%)]';
      default:
        return 'rounded-t-[36px] rounded-b-[14px]';
    }
  };

  // Render nail artwork based on product ID
  const renderNailArt = (id: string, index = 2) => {
    switch (id) {
      case 'prod-1': // Golden Halo Swirls
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#F3E8FF] via-[#FCE7F3] to-[#FEF3C7] overflow-hidden">
            {/* Lilac Marbled Swirls */}
            <svg className="absolute inset-0 w-full h-full opacity-80" viewBox="0 0 100 200" preserveAspectRatio="none">
              <path d="M-10,30 Q40,60 15,110 T80,180" fill="none" stroke="#C084FC" strokeWidth="18" strokeLinecap="round" opacity="0.65" />
              <path d="M110,40 Q60,90 85,140 T20,195" fill="none" stroke="#F472B6" strokeWidth="14" strokeLinecap="round" opacity="0.65" />
              {/* Metallic Gold Ribbons */}
              <path d="M-10,30 Q40,60 15,110 T80,180" fill="none" stroke="#D4AF37" strokeWidth="4" strokeLinecap="round" />
              <path d="M110,40 Q60,90 85,140 T20,195" fill="none" stroke="#F59E0B" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M20,10 Q80,45 40,80 T90,130" fill="none" stroke="#FDE047" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            {/* Gold Stars */}
            <div className="absolute top-4 right-3 text-[#D4AF37] text-xs animate-pulse">✦</div>
            <div className="absolute bottom-10 left-3 text-[#F59E0B] text-xs">✨</div>
            {/* Gloss Highlight */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent opacity-70 pointer-events-none" />
          </div>
        );

      case 'prod-2': // Gymnast Gold Starburst
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#FFFDF5] via-[#FEF3C7] to-[#FDE68A] overflow-hidden">
            {/* Pearl Sparkle texture */}
            <div className="absolute inset-0 bg-[radial-gradient(#F59E0B_1px,transparent_1px)] [background-size:12px_12px] opacity-25" />
            {/* 3D Gold Stars */}
            <div className="absolute inset-0 flex flex-col items-center justify-around py-4">
              <span className="text-[#D4AF37] text-xl drop-shadow-[0_2px_4px_rgba(212,175,55,0.6)]">★</span>
              <span className="text-[#F59E0B] text-sm drop-shadow-[0_2px_3px_rgba(245,158,11,0.6)]">✦</span>
              <span className="text-[#D97706] text-lg drop-shadow-[0_2px_4px_rgba(217,119,6,0.6)]">★</span>
            </div>
            {/* Gold Flakes */}
            <div className="absolute top-8 left-2 w-2 h-2 rounded-full bg-[#F59E0B] opacity-75 blur-[0.5px]" />
            <div className="absolute bottom-6 right-2 w-2.5 h-2.5 rounded-full bg-[#D4AF37] opacity-80 blur-[0.5px]" />
            {/* High Gloss */}
            <div className="absolute top-0 left-1/4 w-1/2 h-full bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none" />
          </div>
        );

      case 'prod-3': // Lilac Dream Waves
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#FAF5FF] via-[#E9D5FF] to-[#C084FC] overflow-hidden">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 200" preserveAspectRatio="none">
              <path d="M0,0 C60,40 20,90 80,140 C120,180 50,200 50,200 L0,200 Z" fill="#A855F7" opacity="0.4" />
              <path d="M100,0 C40,50 90,100 30,160 L100,200 Z" fill="#7E22CE" opacity="0.3" />
              <path d="M0,20 Q60,70 30,130 T100,180" fill="none" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" opacity="0.85" />
              <path d="M10,25 Q70,75 40,135 T110,185" fill="none" stroke="#F3C053" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            <div className="absolute top-5 left-3 text-white text-xs">✧</div>
            <div className="absolute bottom-8 right-3 text-[#F3C053] text-sm">✦</div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/35 to-transparent pointer-events-none" />
          </div>
        );

      case 'prod-4': // Royal Opal & Gold Stud
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#FFFDF0] via-[#FEF3C7] to-[#F3E8FF] overflow-hidden">
            {/* Shimmer Base */}
            <div className="absolute inset-0 bg-gradient-to-br from-white via-[#FDE68A]/30 to-[#DDD6FE]/40" />
            {/* 3D Gold Dome Stud */}
            <div className="absolute top-8 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-gradient-to-br from-[#FEF08A] via-[#D4AF37] to-[#78350F] shadow-[0_4px_8px_rgba(180,83,9,0.5),inset_0_2px_3px_rgba(255,255,255,0.8)] border border-[#FDE047]" />
            {/* Sparkles */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
              <span className="text-[#D4AF37] text-xs block font-serif tracking-widest uppercase opacity-70">Opal Glow</span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none" />
          </div>
        );

      case 'prod-5': // Pastel Sunset Ribbons
        return (
          <div className="relative w-full h-full bg-[#FFFFFF] overflow-hidden">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 200" preserveAspectRatio="none">
              {/* Pink Ribbon */}
              <path d="M-20,40 Q50,70 10,130 T80,210" fill="none" stroke="#F472B6" strokeWidth="22" strokeLinecap="round" opacity="0.8" />
              {/* Mint Ribbon */}
              <path d="M120,30 Q40,90 90,140 T20,210" fill="none" stroke="#5EEAD4" strokeWidth="20" strokeLinecap="round" opacity="0.8" />
              {/* Lilac Ribbon */}
              <path d="M-10,90 Q60,120 20,170 T100,220" fill="none" stroke="#C084FC" strokeWidth="16" strokeLinecap="round" opacity="0.8" />
              {/* Gold Chrome Edges */}
              <path d="M-20,40 Q50,70 10,130 T80,210" fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round" />
              <path d="M120,30 Q40,90 90,140 T20,210" fill="none" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
            </svg>
            <div className="absolute top-4 right-3 text-[#D4AF37] text-xs">✨</div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent pointer-events-none" />
          </div>
        );

      case 'prod-6': // Buttercup & Rose Aura
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#FEF08A] via-[#FDE047] to-[#FCE7F3] overflow-hidden">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 200" preserveAspectRatio="none">
              {/* Pink wave */}
              <path d="M-10,80 C40,60 60,120 110,90 L110,210 L-10,210 Z" fill="#FB7185" opacity="0.75" />
              {/* Gold framing */}
              <path d="M-10,80 C40,60 60,120 110,90" fill="none" stroke="#D4AF37" strokeWidth="4.5" strokeLinecap="round" />
              <path d="M-10,105 C40,85 60,145 110,115" fill="none" stroke="#FEF9C3" strokeWidth="2" strokeLinecap="round" opacity="0.9" />
            </svg>
            <div className="absolute top-5 left-4 text-[#B45309] text-xs">✦</div>
            <div className="absolute bottom-6 right-4 text-white text-xs">✧</div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/40 to-transparent pointer-events-none" />
          </div>
        );

      case 'prod-7': // Iridescent Mermaid Seafoam
        return (
          <div className="relative w-full h-full bg-gradient-to-b from-[#E0F2FE] via-[#A7F3D0] to-[#5EEAD4] overflow-hidden">
            {/* Holographic Chrome texture */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#DDD6FE]/40 via-[#BAE6FD]/50 to-[#FEF08A]/40" />
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 200" preserveAspectRatio="none">
              <path d="M0,50 Q50,30 100,70" fill="none" stroke="#FFFFFF" strokeWidth="3" opacity="0.8" />
              <path d="M0,120 Q50,100 100,140" fill="none" stroke="#FDE047" strokeWidth="2.5" opacity="0.9" />
            </svg>
            <div className="absolute top-6 right-4 text-white text-base drop-shadow-md">✨</div>
            <div className="absolute bottom-8 left-4 text-[#0D9488] text-sm">✧</div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[#D4AF37] text-lg">✦</div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none" />
          </div>
        );

      default: // Custom / Bespoke Set
        return (
          <div className="relative w-full h-full bg-gradient-to-tr from-[#FCE7F3] via-[#EDE9FE] to-[#FEF9C3] overflow-hidden flex flex-col items-center justify-center">
            <div className="text-3xl text-[#D4AF37] animate-bounce">🎨</div>
            <span className="text-[11px] font-bold tracking-wider uppercase text-[#854D0E] mt-1 bg-white/75 px-2 py-0.5 rounded-full shadow-sm">
              Custom Art
            </span>
            <div className="absolute top-2 right-2 text-xs text-[#EC4899]">✨</div>
            <div className="absolute bottom-2 left-2 text-xs text-[#8B5CF6]">✦</div>
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/50 to-transparent pointer-events-none" />
          </div>
        );
    }
  };

  if (!showAllFive) {
    return (
      <div className={`relative flex items-center justify-center p-4 bg-gradient-to-b from-[#FDFBF7] to-[#F5EFE6] rounded-2xl ${className}`}>
        {/* Soft Background Halo */}
        <div className="absolute w-28 h-28 rounded-full bg-gradient-to-tr from-[#FDE68A]/40 via-[#FCE7F3]/40 to-[#DDD6FE]/40 blur-xl pointer-events-none" />
        
        {/* Single Centerpiece Nail */}
        <div
          className={`relative w-20 h-36 shadow-[0_12px_24px_-6px_rgba(45,42,38,0.25),0_0_0_1px_rgba(212,175,55,0.3)] transition-transform duration-300 hover:scale-105 ${getShapeClasses(
            shape
          )}`}
        >
          {renderNailArt(productId)}
          {/* Acrylic 3D Bevel Edge */}
          <div className="absolute inset-0 rounded-[inherit] border border-white/60 pointer-events-none shadow-inner" />
        </div>
      </div>
    );
  }

  // Render Full 5-Finger Set Fan (Thumb to Pinky)
  const sizes = [
    { scale: 'w-10 h-28 -rotate-12 translate-y-3', label: 'Thumb' },
    { scale: 'w-11 h-32 -rotate-6 translate-y-1', label: 'Index' },
    { scale: 'w-12 h-36 rotate-0 -translate-y-1 z-10', label: 'Middle' },
    { scale: 'w-11 h-32 rotate-6 translate-y-1', label: 'Ring' },
    { scale: 'w-9 h-26 rotate-12 translate-y-4', label: 'Pinky' },
  ];

  return (
    <div className={`relative flex items-end justify-center gap-1.5 sm:gap-2.5 p-6 bg-gradient-to-b from-[#FFFDF9] via-[#FAF5EE] to-[#F3EBE0] rounded-3xl overflow-hidden ${className}`}>
      {/* Background Pastel Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#FEF9C3]/50 via-[#FCE7F3]/30 to-transparent pointer-events-none" />
      
      {sizes.map((item, idx) => (
        <div
          key={idx}
          className={`relative ${item.scale} shadow-[0_10px_20px_-4px_rgba(0,0,0,0.2),0_0_0_1px_rgba(212,175,55,0.3)] transition-all duration-300 hover:-translate-y-2 ${getShapeClasses(
            shape
          )}`}
        >
          {renderNailArt(productId, idx)}
          {/* Acrylic Bevel Edge */}
          <div className="absolute inset-0 rounded-[inherit] border border-white/60 pointer-events-none shadow-inner" />
        </div>
      ))}
    </div>
  );
};
